import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Mail, Phone, FileText, Upload, Trash2, Building2, User, Hash, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DocumentUpload from '@/components/DocumentUpload';
import DocumentViewer from '@/components/DocumentViewer';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const statusLabels = {
  protocolo: 'Protocolo',
  em_processo_baixa: 'Em Processo de Baixa',
  concluido: 'Concluído'
};

export default function ClientDetailPage({ user, onLogout }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [services, setServices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
    if (isAdmin) {
      fetchPeriods();
    }
  }, [clientId, isAdmin]);

  const fetchData = async () => {
    try {
      const clientRes = await axios.get(`${API}/clients/${clientId}`, getAuthHeaders());
      setClient(clientRes.data);
      
      try {
        const [servicesRes, documentsRes] = await Promise.all([
          axios.get(`${API}/services/client/${clientId}`, getAuthHeaders()),
          axios.get(`${API}/documents/client/${clientId}`, getAuthHeaders())
        ]);
        setServices(servicesRes.data);
        setDocuments(documentsRes.data);
      } catch (err) {
        // Parceiro não tem acesso a serviços e documentos
        console.log('Acesso negado a serviços/documentos');
      }
    } catch (error) {
      toast.error('Erro ao carregar dados do cliente');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriods = async () => {
    try {
      const response = await axios.get(`${API}/periods`, getAuthHeaders());
      setPeriods(response.data);
    } catch (error) {
      console.log('Erro ao carregar períodos');
    }
  };

  const handleChangePeriod = async (newPeriodId) => {
    try {
      await axios.put(`${API}/clients/${clientId}`, { period_id: newPeriodId }, getAuthHeaders());
      toast.success('Lote do cliente atualizado com sucesso');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar lote do cliente');
    }
  };

  const handleAddService = async () => {
    try {
      await axios.post(`${API}/services`, { client_id: clientId }, getAuthHeaders());
      toast.success('Serviço adicionado com sucesso');
      fetchData();
    } catch (error) {
      toast.error('Erro ao adicionar serviço');
    }
  };

  const handleUpdateServiceStatus = async (serviceId, newStatus) => {
    try {
      await axios.put(`${API}/services/${serviceId}`, { status: newStatus }, getAuthHeaders());
      toast.success('Status atualizado com sucesso');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Tem certeza que deseja remover este serviço?')) {
      try {
        await axios.delete(`${API}/services/${serviceId}`, getAuthHeaders());
        toast.success('Serviço removido com sucesso');
        fetchData();
      } catch (error) {
        toast.error('Erro ao remover serviço');
      }
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, getAuthHeaders());
      toast.success('Documento deletado com sucesso');
      fetchData();
      setSelectedDoc(null);
    } catch (error) {
      toast.error('Erro ao deletar documento');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  const isPessoaFisica = client?.tipo_pessoa === 'fisica';
  const displayName = isPessoaFisica ? client?.nome_completo : client?.razao_social;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/clientes')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              {isPessoaFisica ? (
                <User className="w-6 h-6 text-primary" />
              ) : (
                <Building2 className="w-6 h-6 text-primary" />
              )}
              {displayName}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isPessoaFisica ? 'Pessoa Física' : 'Pessoa Jurídica'}
            </p>
          </div>
        </div>

        {/* Client Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card data-testid="client-info-card">
            <CardHeader>
              <CardTitle>Informações do Cliente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-1">
                    {isPessoaFisica ? 'CPF' : 'CNPJ'}
                  </p>
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-muted-foreground" />
                    <p className="text-base" data-testid="client-doc">
                      {isPessoaFisica ? client?.cpf : client?.cnpj}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-1">Telefone</p>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <p className="text-base" data-testid="client-phone">{client?.telefone}</p>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-1">Email</p>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <p className="text-base" data-testid="client-email">{client?.email}</p>
                  </div>
                </div>
                {client?.partner_email && (
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-1">Cadastrado Por</p>
                    <p className="text-base" data-testid="client-partner">{client.partner_email}</p>
                  </div>
                )}
                {isAdmin && (
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-1">Lote/Período</p>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <Select
                        value={client?.period_id || 'sem_lote'}
                        onValueChange={(value) => handleChangePeriod(value === 'sem_lote' ? null : value)}
                      >
                        <SelectTrigger className="w-[280px]" data-testid="client-period-select">
                          <SelectValue placeholder="Selecionar lote" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sem_lote">Sem lote atribuído</SelectItem>
                          {periods.map((period) => (
                            <SelectItem key={period.id} value={period.id}>
                              {period.name} {period.status === 'inativo' && '(Inativo)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {client?.period_name && (
                        <Badge variant="outline" className="text-primary">
                          {client.period_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Services (Admin Only) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card data-testid="services-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Serviços</CardTitle>
                <Button onClick={handleAddService} className="btn-transition" data-testid="add-service-button">
                  Adicionar Serviço
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {services.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="no-services-message">
                  Nenhum serviço cadastrado
                </div>
              ) : (
                <div className="space-y-3" data-testid="services-list">
                  {services.map((service, index) => (
                    <div
                      key={service.id}
                      className="flex items-center justify-between p-4 border border-border rounded-lg"
                      data-testid={`service-item-${index}`}
                    >
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">
                          Criado em {new Date(service.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <Select
                        value={service.status}
                        onValueChange={(value) => handleUpdateServiceStatus(service.id, value)}
                      >
                        <SelectTrigger className="w-[180px]" data-testid={`service-status-select-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="protocolo" data-testid={`status-option-protocolo-${index}`}>Protocolo</SelectItem>
                          <SelectItem value="em_processo_baixa" data-testid={`status-option-em_processo_baixa-${index}`}>Em Processo de Baixa</SelectItem>
                          <SelectItem value="concluido" data-testid={`status-option-concluido-${index}`}>Concluído</SelectItem>
                        </SelectContent>
                      </Select>
                      <Badge className={`ml-3 status-${service.status}`} data-testid={`service-badge-${index}`}>
                        {statusLabels[service.status]}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteService(service.id)}
                        className="ml-2 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                        title="Remover serviço"
                        data-testid={`delete-service-button-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Documents (Admin Only) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card data-testid="documents-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Documentos</CardTitle>
                <Button
                  onClick={() => setShowUpload(true)}
                  className="btn-transition"
                  data-testid="upload-document-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="no-documents-message">
                  Nenhum documento enviado
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
                  {documents.map((doc, index) => (
                    <div
                      key={doc.id}
                      className="border border-border rounded-lg p-4 card-hover cursor-pointer"
                      onClick={() => setSelectedDoc(doc)}
                      data-testid={`document-item-${index}`}
                    >
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-1" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate" data-testid={`document-name-${index}`}>{doc.filename}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

      {showUpload && (
        <DocumentUpload
          clientId={clientId}
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            fetchData();
          }}
        />
      )}

      {selectedDoc && (
        <DocumentViewer
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
          onDelete={handleDeleteDocument}
        />
      )}
      </div>
    </Layout>
  );
}